// Plausible analytics loader. It runs before Angular bootstraps and never blocks it: the script is
// appended with async and nothing waits for it, so a blocked or unreachable tracker cannot stop the
// application from starting. Single-page navigations are captured by the script itself.

// Routes whose second path segment is an access code. Access codes are bearer credentials, so they
// are replaced by a fixed placeholder before any URL leaves the browser.
const CODE_ROUTES = ['sign', 'signc', 'multisign', 'view', 'd', 'doc-request'];
export const MASKED_CODE = '_code_';

/**
 * Returns the URL without query string and fragment, with the access code of a code route masked.
 * Returns null when the URL cannot be parsed, so an unmasked value is never reported.
 */
export function maskAnalyticsUrl(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const segments = url.pathname.split('/');
  // segments[0] is the empty string before the leading slash; segments[1] is the route name.
  if (CODE_ROUTES.includes(segments[1]) && segments[2]) {
    segments[2] = MASKED_CODE;
  }
  return url.origin + segments.join('/');
}

/** Applied by the tracker to every event: page URL, referrer and the URL of link/download events. */
export function maskAnalyticsRequest(payload: any): any {
  payload.u = maskAnalyticsUrl(payload.u);
  if (!payload.u) {
    return null;
  }
  payload.r = maskAnalyticsUrl(payload.r);
  if (payload.p && typeof payload.p.url === 'string') {
    payload.p.url = maskAnalyticsUrl(payload.p.url);
  }
  return payload;
}

export function loadPlausible(scriptUrl: string, options: { captureOnLocalhost?: boolean } = {}): void {
  if (!scriptUrl) {
    return;
  }
  const w = window as any;
  // Queue stub from the vendor snippet: calls made before the script arrives are replayed by it,
  // and the options given to init are read by the script when it loads.
  w.plausible = w.plausible || function () { (w.plausible.q = w.plausible.q || []).push(arguments); };
  w.plausible.init = w.plausible.init || function (i?: object) { w.plausible.o = i || {}; };
  w.plausible.init({ ...options, transformRequest: maskAnalyticsRequest });

  const script = document.createElement('script');
  script.async = true;
  script.src = scriptUrl;
  document.head.appendChild(script);
}
